import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Tag from 'chameleon/ui-stack/ui/base/tag';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

const TagWithIcon = forwardRef((props, ref) => {
  const {
    icon,
    children,
    iconOrientation,
    iconClassName,
    iconWidth,
    iconHeight,
    isFullWidth,
    isPrimary,
    isSecondary,
    isAlert,
    onClick,
    btnAccessibility,
    ...otherProps
  } = props;

  const isLeftOrientated = iconOrientation === 'left';
  const isRightOrientated = iconOrientation === 'right';

  let iconProps = {};
  if (iconWidth !== null) {
    iconProps.width = iconWidth;
  }
  if (iconHeight !== null) {
    iconProps.height = iconHeight;
  }

  const iconElement = (
    <ButtonWithIcon
      className={cn('absolute h-8.4 w-8.4', {
        'right-0.4': isRightOrientated,
        'left-0.4': isLeftOrientated,
        'hover:bg-black hover:bg-opacity-10': isSecondary,
      })}
      isIcon
      isNaked={isPrimary}
      size="custom"
      onClick={onClick}
      icon={icon}
      iconClassName={iconClassName}
      isAlert={isAlert}
      accessibility={btnAccessibility}
      {...iconProps}
    />
  );

  return (
    <Tag
      ref={ref}
      isPrimary={isPrimary}
      isSecondary={isSecondary}
      isAlert={isAlert}
      isFullWidth={isFullWidth}
      {...otherProps}
    >
      <div
        className={cn('flex items-center', {
          'w-full': isFullWidth,
        })}
      >
        {isLeftOrientated && iconElement}
        <div
          className={cn('text-ellipsis truncate text-center', {
            'w-full': isFullWidth,
            'ml-12.2': isLeftOrientated,
            'mr-12.2': isRightOrientated,
          })}
          data-test-id="ui-tag-with-icon-content"
        >
          {children}
        </div>
        {isRightOrientated && iconElement}
      </div>
    </Tag>
  );
});

TagWithIcon.propTypes = {
  accessibility: PropTypes.shape({
    title: PropTypes.string,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
  }),
  icon: PropTypes.string.isRequired,
  iconOrientation: PropTypes.string,
  iconWidth: PropTypes.string,
  iconHeight: PropTypes.string,
  containerClassName: PropTypes.string,
  isFullWidth: PropTypes.bool,
  isPrimary: PropTypes.bool,
  isSecondary: PropTypes.bool,
  isAlert: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  btnAccessibility: PropTypes.shape({
    title: PropTypes.string,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
  }),
};

TagWithIcon.defaultProps = {
  accessibility: {
    title: null,
    'aria-label': null,
    'aria-labelledby': null,
  },
  iconOrientation: 'left',
  iconWidth: 12,
  iconHeight: 12,
  containerClassName: '',
  isFullWidth: false,
  isPrimary: false,
  isSecondary: false,
  isAlert: false,
  btnAccessibility: {
    title: null,
    'aria-label': null,
    'aria-labelledby': null,
  },
};

TagWithIcon.displayName = 'TagWithIcon';

export default TagWithIcon;
